/** 2307 collection tracker (M04-10): who still owes you a certificate. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Copy, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { Button, Card, EmptyState, Modal, Money, PageHeader, Skeleton } from '../../components/ui';

interface Group {
  contactId: string;
  customer: string;
  email: string;
  year: number;
  quarter: number;
  totalCwt: string;
  receipts: { id: string; number: string; date: string; cwt: string; atc: string | null }[];
  reminder: { subject: string; body: string };
}

export function Missing2307Page(): JSX.Element {
  const list = useQuery({ queryKey: ['missing-2307'], queryFn: () => api<Group[]>('/receipts/missing-2307') });
  const [open, setOpen] = useState<Group | null>(null);
  return (
    <>
      <PageHeader title="2307 Tracker" description="Customers who withheld tax from their payments but have not sent the BIR Form 2307. Without it, the credit cannot be claimed in the SAWT." />
      {list.isLoading && <Skeleton className="h-48 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="Nothing missing" description="Every receipt with withholding has its 2307." /></Card>}
      <div className="grid gap-4 md:grid-cols-2">
        {list.data?.map((g) => (
          <Card key={`${g.contactId}-${g.year}-${g.quarter}`} title={g.customer} description={`Q${g.quarter} ${g.year} · ${g.receipts.length} receipt${g.receipts.length === 1 ? '' : 's'}`} actions={<Button size="sm" variant="secondary" onClick={() => setOpen(g)}><Mail aria-hidden="true" className="h-4 w-4" /> Reminder</Button>}>
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {g.receipts.map((r) => (
                <li key={r.id} className="flex justify-between py-1.5">
                  <Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/receipts/${r.id}`}>{r.number}</Link>
                  <span className="text-slate-500">{shortDate(r.date)} · {r.atc}</span>
                  <Money>{amount(r.cwt)}</Money>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-right text-sm font-semibold">Total <Money>{amount(g.totalCwt)}</Money></p>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title={open.reminder.subject} description={open.email ? `To: ${open.email}` : 'No email on file for this customer.'} onClose={() => setOpen(null)}>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-100 p-3 text-sm dark:bg-slate-800">{open.reminder.body}</pre>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(`${open.reminder.subject}\n\n${open.reminder.body}`)}><Copy aria-hidden="true" className="h-4 w-4" /> Copy</Button>
            {open.email && (
              <a href={`mailto:${open.email}?subject=${encodeURIComponent(open.reminder.subject)}&body=${encodeURIComponent(open.reminder.body)}`}>
                <Button><Mail aria-hidden="true" className="h-4 w-4" /> Open in email</Button>
              </a>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
